import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDateToDDMMYYYY } from "../pages/const";

const BORDER_COLOR = [140, 29, 26]; // #8c1d1a
const HEADER_BG = [240, 230, 210]; // #f0e6d2
const CREDIT_BG = [232, 245, 234]; // #e8f5ea
const DEBIT_BG = [252, 232, 234]; // #fce8ea
const BALANCE_BG = [232, 244, 247]; // #e8f4f7
const OPENING_BG = [227, 242, 253]; // #e3f2fd

const COLS = [
  { key: "date", label: "DATE", w: 14, align: "center" },
  { key: "type", label: "TYPE", w: 18, align: "left" },
  { key: "credit", label: "CREDIT", w: 15, align: "right", kind: "number" },
  { key: "debit", label: "DEBIT", w: 15, align: "right", kind: "number" },
  { key: "balance", label: "BALANCE", w: 15, align: "right", kind: "balance" },
  { key: "remarks", label: "REMARKS", w: 23, align: "left" },
];

function formatDisplayDate(dateString) {
  if (!dateString || dateString === "-") return "-";
  const part = String(dateString).split(" ")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(part)) {
    const [y, m, d] = part.split("-");
    return `${d}-${m}-${y}`;
  }
  return formatDateToDDMMYYYY(part) || part;
}

function formatNumber(value, isMetal = false) {
  if (value === "" || value == null || value === "-") return "-";
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "-";
  const decimals = isMetal ? 3 : 2;
  return Math.abs(n).toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function formatBalanceNumber(value, isMetal = false) {
  if (value === "" || value == null || value === "-") return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  const decimals = isMetal ? 3 : 2;
  if (n === 0) return `0.${"0".repeat(decimals)}`;
  const formatted = Math.abs(n).toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return n < 0 ? `-${formatted}` : formatted;
}

function formatColValue(col, row, isMetal = false) {
  if (!row) return "-";
  if (col.key === "date") {
    if (row.isOpening) return "-";
    return formatDisplayDate(row.date);
  }
  if (col.key === "type") {
    return row.type || (row.isOpening ? "Opening Balance" : "-");
  }
  if (col.key === "remarks") {
    return row.remarks || "-";
  }
  const raw = row[col.key];
  if (col.kind === "number") return formatNumber(raw, isMetal);
  if (col.kind === "balance") return formatBalanceNumber(raw, isMetal);
  if (raw == null || raw === "") return "-";
  return String(raw);
}

export function getFormattedGeneratedOn() {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = now.getFullYear();
  let hours = now.getHours();
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const formattedHours = String(hours).padStart(2, "0");
  return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
}

/**
 * Generates Bank Ledger PDF Blob using high-performance jsPDF + autotable.
 * Supports exact headers, Credit/Debit/Balance column tints, balance coloring,
 * golden summary row with black text, and Indian comma formatting.
 */
export async function generateBankLedgerPdfBlob({
  ledgerData = [],
  bankInfo = {},
  fromDate = "",
  toDate = "",
  entireReport = false,
  isMetalType = false,
  title = "Bank Ledger",
}) {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth(); // 841.89 pt
  const pageHeight = doc.internal.pageSize.getHeight(); // 595.28 pt
  const marginX = 14;
  const availableTableWidth = pageWidth - marginX * 2; // 813.89 pt

  const head = [
    [
      { content: "DATE", styles: { halign: "center" } },
      { content: "TYPE", styles: { halign: "center" } },
      { content: "CREDIT", styles: { halign: "center" } },
      { content: "DEBIT", styles: { halign: "center" } },
      { content: "BALANCE", styles: { halign: "center" } },
      { content: "REMARKS", styles: { halign: "center" } },
    ],
  ];

  const body = ledgerData.map((row) =>
    COLS.map((col) => formatColValue(col, row, isMetalType))
  );

  const columnStyles = {};
  COLS.forEach((col, idx) => {
    columnStyles[idx] = {
      cellWidth: (availableTableWidth * col.w) / 100,
      halign: col.align || "left",
    };
  });

  // Calculate totals for footer row
  let totCredit = 0;
  let totDebit = 0;

  ledgerData.forEach((row) => {
    if (!row.isOpening) {
      totCredit += parseFloat(row.credit || 0) || 0;
      totDebit += parseFloat(row.debit || 0) || 0;
    }
  });

  const lastRow = ledgerData.length > 0 ? ledgerData[ledgerData.length - 1] : null;
  const closingBalance = lastRow ? parseFloat(lastRow.balance || 0) || 0 : 0;

  const foot = [
    [
      "",
      "TOTAL",
      formatNumber(totCredit, isMetalType),
      formatNumber(totDebit, isMetalType),
      formatBalanceNumber(closingBalance, isMetalType),
      "",
    ],
  ];

  const bankName = bankInfo?.bankName || bankInfo?.label || "N/A";
  const accountHolder = bankInfo?.accountHolder || "";
  const accountNumber = bankInfo?.accountNumber || "";

  let dateFilterText = "";
  if (entireReport) {
    dateFilterText = "Entire Report";
  } else if (fromDate || toDate) {
    const formattedFrom = fromDate ? formatDisplayDate(fromDate) : "Beginning";
    const formattedTo = toDate ? formatDisplayDate(toDate) : "Today";
    dateFilterText = `From: ${formattedFrom} To: ${formattedTo}`;
  }

  autoTable(doc, {
    startY: 56,
    head,
    body,
    foot,
    showFoot: "lastPage",
    theme: "grid",
    tableWidth: availableTableWidth,
    margin: { top: 56, left: marginX, right: marginX, bottom: 22 },
    styles: {
      font: "helvetica",
      fontSize: 8,
      cellPadding: { top: 3.5, bottom: 3.5, left: 3, right: 3 },
      lineColor: BORDER_COLOR,
      lineWidth: 0.5,
      textColor: [0, 0, 0],
      valign: "middle",
      overflow: "linebreak",
    },
    headStyles: {
      font: "helvetica",
      fontSize: 8.5,
      fillColor: HEADER_BG,
      textColor: [0, 0, 0],
      fontStyle: "bold",
      lineColor: BORDER_COLOR,
      lineWidth: 0.5,
      halign: "center",
      valign: "middle",
      cellPadding: { top: 4, bottom: 4, left: 2, right: 2 },
    },
    footStyles: {
      font: "helvetica",
      fontSize: 8,
      fillColor: HEADER_BG,
      textColor: [0, 0, 0],
      fontStyle: "bold",
      lineColor: BORDER_COLOR,
      lineWidth: 0.5,
      valign: "middle",
      cellPadding: { top: 4, bottom: 4, left: 2, right: 2 },
    },
    columnStyles,
    didParseCell: (data) => {
      if (data.section === "foot") {
        data.cell.styles.fillColor = HEADER_BG;
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.textColor = [0, 0, 0]; // Keep all totals black

        if (data.column.index === 1) {
          data.cell.styles.halign = "center";
        }
        return;
      }

      if (data.section === "body") {
        const row = ledgerData[data.row.index];
        const colIdx = data.column.index;
        if (!row) return;

        // Background styling
        if (row.isOpening) {
          data.cell.styles.fillColor = OPENING_BG;
          data.cell.styles.fontStyle = "bold";
        } else {
          if (colIdx === 2) {
            data.cell.styles.fillColor = CREDIT_BG;
          } else if (colIdx === 3) {
            data.cell.styles.fillColor = DEBIT_BG;
          } else if (colIdx === 4) {
            data.cell.styles.fillColor = BALANCE_BG;
          } else {
            data.cell.styles.fillColor = [255, 255, 255];
          }
        }

        // Balance text color in body
        if (colIdx === 4 && !row.isOpening) {
          const val = parseFloat(row.balance || 0);
          if (val < 0) data.cell.styles.textColor = [220, 53, 69]; // #dc3545
          else if (val > 0) data.cell.styles.textColor = [40, 167, 69]; // #28a745
        }

        // Single line auto-scaling for number cells
        if (colIdx >= 2 && colIdx <= 4) {
          const rawText = Array.isArray(data.cell.text)
            ? data.cell.text.join(" ")
            : String(data.cell.text ?? "");

          const colDef = COLS[colIdx];
          const colWidth = (availableTableWidth * colDef.w) / 100;
          const maxTextWidth = colWidth - 6;

          doc.setFont("helvetica", data.cell.styles.fontStyle || "normal");
          let fontSize = data.cell.styles.fontSize || 8;
          doc.setFontSize(fontSize);
          let measuredWidth = doc.getTextWidth(rawText);

          while (measuredWidth > maxTextWidth && fontSize > 4.5) {
            fontSize -= 0.3;
            doc.setFontSize(fontSize);
            measuredWidth = doc.getTextWidth(rawText);
          }

          data.cell.styles.fontSize = fontSize;
          data.cell.styles.overflow = "hidden";
          data.cell.text = [rawText];
        }
      }
    },
    didDrawPage: (data) => {
      if (data.pageNumber === 1) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.setTextColor(BORDER_COLOR[0], BORDER_COLOR[1], BORDER_COLOR[2]);
        doc.text(title, pageWidth / 2, 20, { align: "center" });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(30, 30, 30);

        let infoParts = [`Bank: ${bankName}`];
        if (accountHolder) infoParts.push(`Account Holder: ${accountHolder}`);
        if (accountNumber) infoParts.push(`Account Number: ${accountNumber}`);
        doc.text(infoParts.join(" | "), pageWidth / 2, 33, { align: "center" });

        if (dateFilterText) {
          doc.setFontSize(8);
          doc.setTextColor(70, 70, 70);
          doc.text(dateFilterText, pageWidth / 2, 45, { align: "center" });
        }
      } else {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(BORDER_COLOR[0], BORDER_COLOR[1], BORDER_COLOR[2]);
        doc.text(`${title} - ${bankName}`, marginX, 20);
      }
    },
  });

  // Page numbering on all pages
  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(100, 100, 100);
    doc.text(
      `Page ${page} of ${totalPages} (${ledgerData.length} records)`,
      pageWidth - marginX,
      20,
      { align: "right" }
    );
  }

  // On the last page, render "Generated on: DD-MM-YYYY hh:mm A" centered with a 14pt gap
  doc.setPage(totalPages);
  const finalY = doc.lastAutoTable?.finalY || 180;
  const footerY = Math.min(finalY + 14, pageHeight - 8);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(90, 90, 90);
  doc.text(
    `Generated on: ${getFormattedGeneratedOn()}`,
    pageWidth / 2,
    footerY,
    { align: "center" }
  );

  return doc.output("blob");
}

